"use client"

import { useEffect, useRef, useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Loader2 } from "lucide-react"
import { formatCurrency } from "./utils"
import { useTranslations } from "next-intl"


interface MinOrderTotalEditorProps {
  currentValue: number
  onSave: (value: number) => Promise<void>
  isLoading: boolean
  canEdit: boolean
}

export function MinOrderTotalEditor({ currentValue, onSave, isLoading, canEdit }: MinOrderTotalEditorProps) {
  const tUi = useTranslations("Shopify.ShopifyAttributionDashboardMinOrderTotalEditor")
  const tCommonUi = useTranslations("Common")

  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState<number>(currentValue)
  const [isSaving, setIsSaving] = useState(false)
  const isEditingRef = useRef(isEditing)

  useEffect(() => {
    isEditingRef.current = isEditing
  }, [isEditing])

  useEffect(() => {
    if (isEditingRef.current) return
    setDraft(Number.isFinite(currentValue) ? currentValue : 0)
  }, [currentValue])

  const handleEditStart = () => {
    if (isLoading || isSaving) return
    setIsEditing(true)
  }

  const handleCancel = () => {
    setDraft(Number.isFinite(currentValue) ? currentValue : 0)
    setIsEditing(false)
  }

  const handleSave = async () => {
    const sanitizedValue = Number.isFinite(draft) && draft > 0 ? draft : 0

    if (sanitizedValue === currentValue) {
      handleCancel()
      return
    }

    setIsSaving(true)
    try {
      await onSave(sanitizedValue)
      setDraft(sanitizedValue)
      setIsEditing(false)
    } catch (error) {
      console.error("Failed to update minimum order filter:", error)
      setDraft(Number.isFinite(currentValue) ? currentValue : 0)
    } finally {
      setIsSaving(false)
    }
  }

  if (isEditing) {
    return (
      <div className="flex items-center gap-2">
        <Input
          type="number"
          min="0"
          step="0.01"
          className="w-[160px]"
          value={Number.isNaN(draft) ? 0 : draft}
          onFocus={(event) => {
            if (event.target.value === "0") event.target.value = ""
          }}
          onBlur={(event) => {
            if (event.target.value === "") event.target.value = "0"
          }}
          onChange={(event) => {
            const nextValue = Number.parseFloat(event.target.value)
            setDraft(Number.isNaN(nextValue) ? 0 : nextValue)
          }}
          disabled={isSaving}
          autoFocus
        />
        <Button size="sm" onClick={handleSave} disabled={isSaving}>
          {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          <span><span key={tUi("save")}>{tUi("save")}</span></span>
        </Button>
        <Button size="sm" variant="outline" onClick={handleCancel} disabled={isSaving}>
          <span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></Button>
      </div>
    )
  }

  return (
    <Button
      variant="ghost"
      size="sm"
      className="text-sm"
      onClick={handleEditStart}
      disabled={!canEdit || isLoading || isSaving}
    >
      {formatCurrency(Number.isFinite(draft) ? draft : 0)}
    </Button>
  )
}
